import { useEffect, useMemo, useState } from 'react';

import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import SectionWrapper from '../../../components/SectionWrapper/SectionWrapper';
import BasicPagination from '../../../components/DataTable/BasicPagination';
import useRestaurantInventory from '../../../hooks/restaurant/useRestaurantInventory';
import useDebounced from '../../../hooks/useDebounced';
import WorkspaceLayout from '../../../layout/WorkspaceLayout';
import BranchService from '../../../services/branch/BranchService';
import { useAuthUser } from '../../../hooks/auth/useAuthUser';

import { IBranchDropdown } from '../../../types/branches/branchInterfaces';
import { RestaurantInventoryFilters } from '../../../types/restaurant/RestaurantInventoryTypes';

import { sumDecimalProducts } from '../../../utils/decimalValue';
import { RESTAURANT_INVENTORY_UPDATED_EVENT } from '../production/restaurantInventoryEvents';

import RestaurantInventoryFormModal from './RestaurantInventoryFormModal';

import InventoryHeader from './components/InventoryHeader';
import InventoryFilters from './components/InventoryFilters';
import InventoryGrid from './components/InventoryGrid';
import InventoryTable from './components/InventoryTable';
import InventorySummary from './components/InventorySummary';
import InventorySkeleton from './components/InventorySkeleton';
import InventoryEmpty from './components/InventoryEmpty';
import { InventoryViewFilter } from './types';
import InventoryBranchEmpty from './components/InventoryBranchEmpty';

const RestaurantInventoryScreen = () => {
  // =====================================================
  // 🔹 Sucursales
  // =====================================================

  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [branches, setBranches] = useState<
    IBranchDropdown[]
  >([]);

  const [branchId, setBranchId] = useState<
    number | ''
  >('');

  const [loadingBranches, setLoadingBranches] =
    useState(true);

  const [branchError, setBranchError] = useState('');

  // =====================================================
  // 🔹 Filtros
  // =====================================================

  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  const [viewFilter, setViewFilter] =
    useState<InventoryViewFilter>('ACTIVE');

  // =====================================================
  // 🔹 Hooks
  // =====================================================

  const inventory = useRestaurantInventory(branchId);

  const debouncedSearch = useDebounced(search, 350);

  const normalizedSearch =
    search === '' ? '' : debouncedSearch;

  // =====================================================
  // 🔹 Cargar sucursales
  // =====================================================

  useEffect(() => {
    setBranchError('');

    const request = canPickBranch
      ? BranchService.fetchDropdown()
      : BranchService.fetchMyBranch().then((mine) => [mine]);

    request
      .then((items) => {
        setBranches(items);

        if (items.length > 0) {
          setBranchId(items[0].id);
        }
      })
      .catch((error: any) => {
        setBranchError(
          error?.response?.data?.message ||
            'No fue posible cargar las sucursales.'
        );
      })
      .finally(() => setLoadingBranches(false));
  }, [canPickBranch]);

  // =====================================================
  // 🔹 Filtros para backend
  // =====================================================

  const filters = useMemo<RestaurantInventoryFilters>(
    () => ({
      categoryId: categoryId || undefined,

      activeOnly: viewFilter === 'ACTIVE' || viewFilter === 'LOW',

      lowStockOnly: viewFilter === 'LOW',

      search: normalizedSearch.trim() || undefined,
      page,
      size,
    }),
    [
      categoryId,
      normalizedSearch,
      page,
      size,
      viewFilter,
    ]
  );

  // =====================================================
  // 🔹 Cargar inventario
  // =====================================================

  useEffect(() => {
    if (branchId) {
      inventory.load(filters);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, filters]);

  // =====================================================
  // 🔹 Actualización desde otros flujos
  // =====================================================

  useEffect(() => {
    const reload = () => {
      if (branchId) {
        void inventory.load(filters);
      }
    };

    window.addEventListener(
      RESTAURANT_INVENTORY_UPDATED_EVENT,
      reload
    );

    return () => {
      window.removeEventListener(
        RESTAURANT_INVENTORY_UPDATED_EVENT,
        reload
      );
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, filters]);

  // =====================================================
  // 🔹 Registros visibles
  // =====================================================

  const visibleItems = inventory.list;

  // =====================================================
  // 🔹 Indicadores
  // =====================================================

  const availableCount =
    inventory.rawMaterials.filter(
      (item) =>
        item.active &&
        !inventory.existingRawMaterialIds.includes(
          item.id
        )
    ).length;

  const lowStockCount = visibleItems.filter(
    (item) => item.active && item.lowStock
  ).length;

  const totalValue = sumDecimalProducts(
    visibleItems.map((item) => ({
      quantity: item.currentStock,
      unitCost: item.averageCost,
    }))
  );

  const createDisabled =
    !branchId || availableCount === 0;

  // =====================================================
  // 🔹 Eventos
  // =====================================================

  const resetFilters = () => {
    setSearch('');
    setCategoryId(0);
    setViewFilter('ACTIVE');
    setPage(0);
  };

  const changeBranch = (
    nextBranchId: number
  ) => {
    resetFilters();
    setBranchId(nextBranchId);
  };

  const selectLowStock = () => {
    setViewFilter('LOW');
    setPage(0);
  };

  // =====================================================
  // 🔹 Sin sucursales
  // =====================================================

  if (!loadingBranches && !branchError && !branchId) {
    return (
      <WorkspaceLayout>
        <BreadcrumbTwo />

        <SectionWrapper>
          <InventoryBranchEmpty />
        </SectionWrapper>
      </WorkspaceLayout>
    );
  }

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        <InventoryHeader
          branches={branches}
          branchId={branchId}
          loadingBranches={loadingBranches}
          branchError={branchError}
          canPickBranch={canPickBranch}
          createDisabled={createDisabled}
          onBranchChange={changeBranch}
          onCreate={inventory.openCreate}
        />

        {branchId && (
          <>
            <InventorySummary
              visibleCount={visibleItems.length}
              lowStockCount={lowStockCount}
              totalValue={totalValue}
              lowStockSelected={
                viewFilter === 'LOW'
              }
              onLowStockClick={selectLowStock}
            />

            <InventoryFilters
              search={search}
              categoryId={categoryId}
              viewFilter={viewFilter}
              categories={inventory.categories}
              onSearchChange={(value) => { setSearch(value); setPage(0); }}
              onCategoryChange={(value) => { setCategoryId(value); setPage(0); }}
              onViewChange={(value) => { setViewFilter(value); setPage(0); }}
            />

            {inventory.loading ? (
              <InventorySkeleton />
            ) : visibleItems.length === 0 ? (
              <InventoryEmpty />
            ) : (
              <>
                <InventoryTable
                  items={visibleItems}
                  onEdit={inventory.edit}
                  onToggleActive={(item) =>
                    inventory.toggleActive(item, filters)
                  }
                />
                <InventoryGrid
                  items={visibleItems}
                  onEdit={inventory.edit}
                  onToggleActive={(item) =>
                    inventory.toggleActive(item, filters)
                  }
                />
              </>
            )}

            {inventory.page && (
              <BasicPagination
                currentPage={inventory.page.number}
                totalPages={inventory.page.totalPages}
                totalElements={inventory.page.totalElements}
                size={inventory.page.size}
                first={inventory.page.number === 0}
                last={inventory.page.number >= inventory.page.totalPages - 1}
                onPageChange={setPage}
                onSizeChange={(value) => { setSize(value); setPage(0); }}
              />
            )}
          </>
        )}
      </SectionWrapper>

      <RestaurantInventoryFormModal
        isOpen={inventory.modalOpen}
        form={inventory.form}
        rawMaterials={inventory.rawMaterials}
        unavailableRawMaterialIds={
          inventory.existingRawMaterialIds
        }
        saving={inventory.saving}
        onClose={inventory.closeModal}
        onChange={inventory.updateField}
        onSave={() => inventory.save(filters)}
      />
    </WorkspaceLayout>
  );
};

export default RestaurantInventoryScreen;
